import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Send, Edit3, Plus, Trash2, ChevronDown, ChevronUp, Loader2, CheckCircle2, AlertCircle,
  Copy, Eye, EyeOff, Upload, Code, Download, Check, X
} from 'lucide-react';

interface WebhookConfigProps {
  guildId: string;
  guildData?: any;
}

interface EmbedField {
  name: string;
  value: string;
  inline: boolean;
}

interface EmbedData {
  title: string;
  description: string;
  color: string;
  url: string;
  authorName: string;
  authorIconUrl: string;
  authorUrl: string;
  thumbnailUrl: string;
  imageUrl: string;
  footerText: string;
  footerIconUrl: string;
  fields: EmbedField[];
  expanded: boolean;
}

const EMPTY_EMBED: EmbedData = {
  title: '', description: '', color: '#5865F2', url: '',
  authorName: '', authorIconUrl: '', authorUrl: '',
  thumbnailUrl: '', imageUrl: '',
  footerText: '', footerIconUrl: '',
  fields: [], expanded: true,
};

/* ─── Markdown renderer (Discord-like with blockquotes) ─── */
function md(text: string): string {
  let html = text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Blockquotes (> text)
  html = html.replace(/^> (.+)$/gm, '<div class="border-l-4 border-[#4e5058] pl-3 text-[#b5bac1]">$1</div>');

  // Inline formatting
  html = html
    .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/__(.*?)__/g, '<u>$1</u>')
    .replace(/~~(.*?)~~/g, '<del>$1</del>')
    .replace(/`([^`]+)`/g, '<code class="bg-[#1e1f22] px-1 py-0.5 rounded text-[12px] font-mono">$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#00AFF4] hover:underline">$1</a>')
    .replace(/\n/g, '<br/>');

  return html;
}

/* ─── Section wrapper ─── */
function Section({ label, children, defaultOpen = false }: { label: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/[0.05] rounded-lg overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-[12px] text-gray-400 font-medium hover:text-gray-200 transition-colors bg-dark-800/30">
        {label}
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && <div className="p-3 space-y-2.5 bg-dark-800/20">{children}</div>}
    </div>
  );
}

/* ─── Input ─── */
function Input({ label, value, onChange, placeholder, type = 'text' }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string;
}) {
  return (
    <div>
      <label className="text-gray-500 text-[11px] font-medium block mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-dark-900/60 border border-white/[0.06] rounded-md px-2.5 py-1.5 text-white text-[13px] placeholder-gray-700 outline-none focus:border-primary/40 transition-colors" />
    </div>
  );
}

/* ─── Textarea ─── */
function Textarea({ label, value, onChange, placeholder, rows = 3 }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; rows?: number;
}) {
  return (
    <div>
      <label className="text-gray-500 text-[11px] font-medium block mb-1">{label}</label>
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        className="w-full bg-dark-900/60 border border-white/[0.06] rounded-md px-2.5 py-1.5 text-white text-[13px] placeholder-gray-700 outline-none focus:border-primary/40 transition-colors resize-none font-mono" />
    </div>
  );
}

/* ─── Live Discord Preview ─── */
function DiscordPreview({ content, embeds, username, avatarUrl }: {
  content: string; embeds: EmbedData[]; username: string; avatarUrl: string;
}) {
  const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const hasAnything = content.trim() || embeds.some(e => e.title || e.description || e.authorName || e.fields.length || e.footerText || e.imageUrl || e.thumbnailUrl);

  return (
    <div className="bg-[#313338] rounded-lg p-4 min-h-[120px]">
      {!hasAnything ? (
        <p className="text-[#949ba4] text-[13px] text-center py-8">Your message preview will appear here</p>
      ) : (
        <div className="flex gap-3">
          <img src={avatarUrl || 'https://cdn.discordapp.com/embed/avatars/0.png'} alt=""
            className="w-10 h-10 rounded-full shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-white font-medium text-[15px]">{username || 'Webhook'}</span>
              <span className="bg-[#5865F2] text-white text-[10px] font-medium px-1 py-[1px] rounded leading-tight">BOT</span>
              <span className="text-[#949ba4] text-[11px]">Today at {now}</span>
            </div>

            {content.trim() && (
              <p className="text-[#dbdee1] text-[15px] leading-[1.375rem] whitespace-pre-wrap break-words"
                dangerouslySetInnerHTML={{ __html: md(content) }} />
            )}

            {embeds.map((embed, i) => {
              const has = embed.title || embed.description || embed.authorName || embed.fields.length || embed.footerText || embed.imageUrl || embed.thumbnailUrl;
              if (!has) return null;
              return (
                <div key={i} className="bg-[#2b2d31] rounded overflow-hidden border-l-[4px] max-w-[520px] mt-1"
                  style={{ borderColor: embed.color || '#202225' }}>
                  <div className="p-3 flex gap-4">
                    <div className="flex-1 min-w-0 space-y-1">
                      {embed.authorName && (
                        <div className="flex items-center gap-2">
                          {embed.authorIconUrl && <img src={embed.authorIconUrl} alt="" className="w-6 h-6 rounded-full" />}
                          {embed.authorUrl ? (
                            <a href={embed.authorUrl} className="text-white text-[13px] font-medium hover:underline">{embed.authorName}</a>
                          ) : (
                            <span className="text-white text-[13px] font-medium">{embed.authorName}</span>
                          )}
                        </div>
                      )}
                      {embed.title && (
                        embed.url ? (
                          <a href={embed.url} className="text-[#00AFF4] text-[15px] font-semibold hover:underline block">{embed.title}</a>
                        ) : (
                          <p className="text-white text-[15px] font-semibold">{embed.title}</p>
                        )
                      )}
                      {embed.description && (
                        <p className="text-[#dbdee1] text-[14px] leading-snug whitespace-pre-wrap"
                          dangerouslySetInnerHTML={{ __html: md(embed.description) }} />
                      )}
                      {embed.fields.length > 0 && (
                        <div className="grid gap-2 mt-2" style={{
                          gridTemplateColumns: embed.fields.some(f => f.inline) ? 'repeat(3, 1fr)' : '1fr'
                        }}>
                          {embed.fields.map((f, fi) => (
                            <div key={fi} style={{ gridColumn: f.inline ? undefined : '1 / -1' }}>
                              <p className="text-white text-[13px] font-semibold">{f.name || '\u200b'}</p>
                              <p className="text-[#dbdee1] text-[13px] whitespace-pre-wrap"
                                dangerouslySetInnerHTML={{ __html: md(f.value || '\u200b') }} />
                            </div>
                          ))}
                        </div>
                      )}
                      {embed.imageUrl && <img src={embed.imageUrl} alt="" className="w-full max-w-[400px] rounded mt-2" />}
                      {embed.footerText && (
                        <div className="flex items-center gap-2 mt-2">
                          {embed.footerIconUrl && <img src={embed.footerIconUrl} alt="" className="w-5 h-5 rounded-full" />}
                          <span className="text-[#949ba4] text-[11px]">{embed.footerText}</span>
                        </div>
                      )}
                    </div>
                    {embed.thumbnailUrl && <img src={embed.thumbnailUrl} alt="" className="w-20 h-20 rounded object-cover shrink-0" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Main Component                                                           */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function WebhookConfig({ guildId }: WebhookConfigProps) {
  const { token } = useAuth();
  const [webhookUrl, setWebhookUrl] = useState('');
  const [content, setContent] = useState('');
  const [embeds, setEmbeds] = useState<EmbedData[]>([]);
  const [username, setUsername] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [editMessageUrl, setEditMessageUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<{ type: 'idle' | 'success' | 'error'; msg: string }>({ type: 'idle', msg: '' });
  const [previewVisible, setPreviewVisible] = useState(true);
  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
  const [jsonEditorText, setJsonEditorText] = useState('');
  const [jsonEditorError, setJsonEditorError] = useState('');
  const [jsonApplied, setJsonApplied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fetchedWebhookUrl = useRef<string>('');

  // Auto-fetch webhook info when a valid URL is entered
  useEffect(() => {
    const url = webhookUrl.trim();
    if (!url || fetchedWebhookUrl.current === url) return;
    const isValidWebhook = /^https:\/\/discord\.com\/api\/webhooks\/\d+\/[^/]+$/.test(url);
    if (!isValidWebhook) return;

    fetchedWebhookUrl.current = url;
    fetch(url)
      .then(r => r.ok ? r.json() : null)
      .then((data: any) => {
        if (!data) return;
        if (data.name) setUsername(data.name);
        if (data.avatar && data.id) {
          setAvatarUrl(`https://cdn.discordapp.com/avatars/${data.id}/${data.avatar}.png?size=128`);
        }
      })
      .catch(() => {/* silently ignore */ });
  }, [webhookUrl]);

  // Parse message URL: https://discord.com/api/webhooks/{webhook_id}/{token}/messages/{message_id}
  function parseMessageUrl(url: string): { webhookUrl: string; messageId: string } | null {
    const match = url.match(/https:\/\/discord\.com\/api\/webhooks\/(\d+)\/([^\/]+)\/messages\/(\d+)/);
    if (!match) return null;
    const [, webhookId, token, messageId] = match;
    return {
      webhookUrl: `https://discord.com/api/webhooks/${webhookId}/${token}`,
      messageId,
    };
  }

  function updateEmbed(i: number, partial: Partial<EmbedData>) {
    setEmbeds(prev => prev.map((e, idx) => idx === i ? { ...e, ...partial } : e));
  }
  function removeEmbed(i: number) { setEmbeds(prev => prev.filter((_, idx) => idx !== i)); }
  function addField(ei: number) {
    updateEmbed(ei, { fields: [...embeds[ei].fields, { name: '', value: '', inline: false }] });
  }
  function updateField(ei: number, fi: number, partial: Partial<EmbedField>) {
    const fields = embeds[ei].fields.map((f, idx) => idx === fi ? { ...f, ...partial } : f);
    updateEmbed(ei, { fields });
  }
  function removeField(ei: number, fi: number) {
    updateEmbed(ei, { fields: embeds[ei].fields.filter((_, idx) => idx !== fi) });
  }

  // Sync JSON editor text whenever the editor is opened or payload changes
  const buildPayloadStr = useCallback(() => JSON.stringify(buildPayload(), null, 2), [content, username, avatarUrl, embeds]);

  useEffect(() => {
    if (jsonEditorOpen) {
      setJsonEditorText(buildPayloadStr());
      setJsonEditorError('');
    }
  }, [jsonEditorOpen]);

  function applyJsonEdits() {
    try {
      const json = JSON.parse(jsonEditorText);
      if (json.content !== undefined) setContent(json.content);
      if (json.username !== undefined) setUsername(json.username);
      if (json.avatar_url !== undefined) setAvatarUrl(json.avatar_url);
      if (Array.isArray(json.embeds)) {
        const loaded = json.embeds.map((e: any) => ({
          title: e.title || '',
          description: e.description || '',
          color: e.color ? `#${e.color.toString(16).padStart(6, '0')}` : '#5865F2',
          url: e.url || '',
          authorName: e.author?.name || '',
          authorIconUrl: e.author?.icon_url || '',
          authorUrl: e.author?.url || '',
          thumbnailUrl: e.thumbnail?.url || '',
          imageUrl: e.image?.url || '',
          footerText: e.footer?.text || '',
          footerIconUrl: e.footer?.icon_url || '',
          fields: e.fields || [],
          expanded: true,
        }));
        setEmbeds(loaded);
      }
      setJsonEditorError('');
      setJsonApplied(true);
      setTimeout(() => setJsonApplied(false), 1500);
    } catch (err: any) {
      setJsonEditorError(`Invalid JSON: ${err.message}`);
    }
  }

  function downloadJson() {
    const blob = new Blob([jsonEditorText], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'webhook-message.json';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function buildPayload() {
    const payload: any = {};
    if (content.trim()) payload.content = content;
    if (username.trim()) payload.username = username;
    if (avatarUrl.trim()) payload.avatar_url = avatarUrl;

    const builtEmbeds = embeds
      .filter(e => e.title || e.description || e.authorName || e.fields.length || e.footerText || e.imageUrl)
      .map(e => {
        const obj: any = {};
        if (e.title) obj.title = e.title;
        if (e.description) obj.description = e.description;
        if (e.color) obj.color = parseInt(e.color.replace('#', ''), 16);
        if (e.url) obj.url = e.url;
        if (e.authorName) {
          obj.author = { name: e.authorName };
          if (e.authorIconUrl) obj.author.icon_url = e.authorIconUrl;
          if (e.authorUrl) obj.author.url = e.authorUrl;
        }
        if (e.thumbnailUrl) obj.thumbnail = { url: e.thumbnailUrl };
        if (e.imageUrl) obj.image = { url: e.imageUrl };
        if (e.footerText) {
          obj.footer = { text: e.footerText };
          if (e.footerIconUrl) obj.footer.icon_url = e.footerIconUrl;
        }
        if (e.fields.length) obj.fields = e.fields.filter(f => f.name || f.value);
        return obj;
      });

    if (builtEmbeds.length) payload.embeds = builtEmbeds;
    return payload;
  }

  async function handleSend() {
    if (!webhookUrl.trim()) { setStatus({ type: 'error', msg: 'Enter a webhook URL' }); return; }
    const payload = buildPayload();
    if (!payload.content && !payload.embeds?.length) { setStatus({ type: 'error', msg: 'Message is empty' }); return; }

    setSending(true); setStatus({ type: 'idle', msg: '' });
    try {
      const API_BASE = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${API_BASE}/api/dashboard/${guildId}/webhook-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ webhook_url: webhookUrl, ...payload }),
      });
      const data = await res.json();
      if (res.ok) {
        const messageUrl = `${webhookUrl}/messages/${data.message_id}`;
        setStatus({ type: 'success', msg: `Sent! Message URL copied to clipboard` });
        setEditMessageUrl(messageUrl);
        navigator.clipboard.writeText(messageUrl);
      } else {
        setStatus({ type: 'error', msg: data.error || 'Failed to send' });
      }
    } catch (e: any) {
      setStatus({ type: 'error', msg: e.message || 'Network error' });
    } finally {
      setSending(false);
    }
  }

  async function handleEdit() {
    if (!editMessageUrl.trim()) {
      setStatus({ type: 'error', msg: 'Enter a message URL to edit' }); return;
    }
    const parsed = parseMessageUrl(editMessageUrl);
    if (!parsed) {
      setStatus({ type: 'error', msg: 'Invalid message URL format' }); return;
    }
    const payload = buildPayload();
    setSending(true); setStatus({ type: 'idle', msg: '' });
    try {
      const API_BASE = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${API_BASE}/api/dashboard/${guildId}/webhook-edit`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ webhook_url: parsed.webhookUrl, message_id: parsed.messageId, ...payload }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus({ type: 'success', msg: 'Message edited!' });
      } else {
        setStatus({ type: 'error', msg: data.error || 'Failed to edit' });
      }
    } catch (e: any) {
      setStatus({ type: 'error', msg: e.message || 'Network error' });
    } finally {
      setSending(false);
    }
  }

  // @ts-ignore — kept for future toolbar button
  function handleCopyJson() {
    navigator.clipboard.writeText(JSON.stringify(buildPayload(), null, 2));
    setStatus({ type: 'success', msg: 'JSON copied to clipboard' });
  }

  function handleLoadJson(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target?.result as string);
        if (json.content) setContent(json.content);
        if (json.username) setUsername(json.username);
        if (json.avatar_url) setAvatarUrl(json.avatar_url);
        if (json.embeds) {
          const loaded = json.embeds.map((e: any) => ({
            title: e.title || '',
            description: e.description || '',
            color: e.color ? `#${e.color.toString(16).padStart(6, '0')}` : '#5865F2',
            url: e.url || '',
            authorName: e.author?.name || '',
            authorIconUrl: e.author?.icon_url || '',
            authorUrl: e.author?.url || '',
            thumbnailUrl: e.thumbnail?.url || '',
            imageUrl: e.image?.url || '',
            footerText: e.footer?.text || '',
            footerIconUrl: e.footer?.icon_url || '',
            fields: e.fields || [],
            expanded: true,
          }));
          setEmbeds(loaded);
        }
        setStatus({ type: 'success', msg: 'JSON loaded successfully' });
      } catch (err: any) {
        setStatus({ type: 'error', msg: `Invalid JSON: ${err.message}` });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-semibold text-lg">Webhooks</h2>
          <p className="text-gray-500 text-[12px]">Send and edit webhook messages with embeds</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => { setJsonEditorText(buildPayloadStr()); setJsonEditorOpen(o => !o); setJsonEditorError(''); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] border rounded-lg transition-colors ${jsonEditorOpen
                ? 'text-primary bg-primary/10 border-primary/25'
                : 'text-gray-400 bg-dark-800/50 border-white/[0.06] hover:text-white hover:border-white/10'
              }`}>
            <Code className="w-3.5 h-3.5" /> JSON
          </button>
          <button type="button" onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] text-gray-400 bg-dark-800/50 border border-white/[0.06] rounded-lg hover:text-white hover:border-white/10 transition-colors">
            <Upload className="w-3.5 h-3.5" /> Load JSON
          </button>
          <input ref={fileInputRef} type="file" accept=".json" onChange={handleLoadJson} className="hidden" />
          <button type="button" onClick={() => setPreviewVisible(!previewVisible)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] text-gray-400 bg-dark-800/50 border border-white/[0.06] rounded-lg hover:text-white hover:border-white/10 transition-colors">
            {previewVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            Preview
          </button>
        </div>
      </div>

      {/* ─── JSON Editor Panel ─── */}
      {jsonEditorOpen && (
        <div className="bg-dark-800/50 border border-primary/20 rounded-xl overflow-hidden animate-fade-in">
          {/* toolbar */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.05] bg-dark-900/40">
            <div className="flex items-center gap-2">
              <Code className="w-3.5 h-3.5 text-primary" />
              <span className="text-[12px] font-medium text-white">Raw JSON</span>
              <span className="text-[10px] text-gray-600">edit &amp; apply to update the form</span>
            </div>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => { navigator.clipboard.writeText(jsonEditorText); }}
                className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-gray-500 hover:text-white border border-white/[0.06] rounded-md transition-colors">
                <Copy className="w-3 h-3" /> Copy
              </button>
              <button type="button" onClick={downloadJson}
                className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-gray-500 hover:text-white border border-white/[0.06] rounded-md transition-colors">
                <Download className="w-3 h-3" /> Download
              </button>
              <button type="button" onClick={applyJsonEdits}
                className={`flex items-center gap-1 px-2.5 py-1 text-[11px] border rounded-md transition-colors ${jsonApplied ? 'text-green-400 border-green-500/30 bg-green-500/10' : 'text-primary border-primary/30 hover:bg-primary/10'
                  }`}>
                {jsonApplied ? <Check className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                {jsonApplied ? 'Applied!' : 'Apply'}
              </button>
              <button type="button" onClick={() => setJsonEditorOpen(false)}
                className="p-1 text-gray-600 hover:text-white transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <textarea
            value={jsonEditorText}
            onChange={e => { setJsonEditorText(e.target.value); setJsonEditorError(''); }}
            spellCheck={false}
            className="w-full bg-transparent text-[12px] text-gray-300 font-mono px-4 py-3 resize-none outline-none min-h-[220px] max-h-[420px] overflow-y-auto"
            style={{ tabSize: 2 }}
          />
          {jsonEditorError && (
            <div className="px-4 py-2 border-t border-red-500/20 bg-red-500/5 text-red-400 text-[11px]">{jsonEditorError}</div>
          )}
        </div>
      )}

      <div className={`grid gap-4 ${previewVisible ? 'grid-cols-1 xl:grid-cols-[1fr_420px]' : 'grid-cols-1 max-w-2xl'}`}>
        {/* ─── Editor Column ─── */}
        <div className="space-y-3">
          {/* Webhook URL + Message URL for editing */}
          <div className="bg-dark-800/40 border border-white/[0.05] rounded-lg p-3 space-y-2.5">
            <div className="flex items-center gap-2">
              <Send className="w-4 h-4 text-gray-600 shrink-0" />
              <input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)}
                placeholder="Webhook URL: https://discord.com/api/webhooks/..."
                className="flex-1 bg-dark-900/60 border border-white/[0.06] rounded-md px-2.5 py-1.5 text-white text-[13px] placeholder-gray-700 outline-none focus:border-primary/40 transition-colors font-mono" />
            </div>
            <div className="flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-gray-600 shrink-0" />
              <input value={editMessageUrl} onChange={e => setEditMessageUrl(e.target.value)}
                placeholder="Message URL (for editing): https://discord.com/api/webhooks/.../messages/..."
                className="flex-1 bg-dark-900/60 border border-white/[0.06] rounded-md px-2.5 py-1.5 text-white text-[13px] placeholder-gray-700 outline-none focus:border-primary/40 transition-colors font-mono" />
            </div>
          </div>

          {/* Profile Override */}
          <Section label="Profile Override" defaultOpen={false}>
            <div className="grid grid-cols-2 gap-2.5">
              <Input label="Username" value={username} onChange={setUsername} placeholder="Custom name" />
              <Input label="Avatar URL" value={avatarUrl} onChange={setAvatarUrl} placeholder="https://..." />
            </div>
          </Section>

          {/* Message Content */}
          <div className="bg-dark-800/40 border border-white/[0.05] rounded-lg p-3">
            <Textarea label="Message Content" value={content} onChange={setContent} placeholder="Hello world!" rows={3} />
          </div>

          {/* Embeds */}
          {embeds.map((embed, ei) => (
            <div key={ei} className="bg-dark-800/40 border border-white/[0.05] rounded-lg overflow-hidden">
              {/* Embed header */}
              <div className="flex items-center justify-between px-3 py-2 bg-dark-800/40 border-b border-white/[0.04]">
                <button type="button" onClick={() => updateEmbed(ei, { expanded: !embed.expanded })}
                  className="flex items-center gap-2 text-[13px] text-white font-medium hover:text-primary transition-colors">
                  {embed.expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  Embed {ei + 1}
                </button>
                <div className="flex items-center gap-2">
                  <input type="color" value={embed.color} onChange={e => updateEmbed(ei, { color: e.target.value })}
                    className="w-6 h-6 rounded cursor-pointer border-none bg-transparent" />
                  <button type="button" onClick={() => removeEmbed(ei)}
                    className="p-1 text-gray-600 hover:text-red-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {embed.expanded && (
                <div className="p-3 space-y-3">
                  {/* Author */}
                  <Section label="Author">
                    <Input label="Name" value={embed.authorName} onChange={v => updateEmbed(ei, { authorName: v })} placeholder="Author name" />
                    <div className="grid grid-cols-2 gap-2.5">
                      <Input label="URL" value={embed.authorUrl} onChange={v => updateEmbed(ei, { authorUrl: v })} placeholder="https://..." />
                      <Input label="Icon URL" value={embed.authorIconUrl} onChange={v => updateEmbed(ei, { authorIconUrl: v })} placeholder="https://..." />
                    </div>
                  </Section>

                  {/* Title + Description */}
                  <Input label="Title" value={embed.title} onChange={v => updateEmbed(ei, { title: v })} placeholder="Embed title" />
                  <Input label="URL" value={embed.url} onChange={v => updateEmbed(ei, { url: v })} placeholder="https://..." />
                  <Textarea label="Description" value={embed.description} onChange={v => updateEmbed(ei, { description: v })} placeholder="Embed description... supports **markdown**" rows={4} />

                  {/* Fields */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-gray-500 text-[11px] font-medium">Fields</p>
                      <button type="button" onClick={() => addField(ei)}
                        className="flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 transition-colors">
                        <Plus className="w-3 h-3" /> Add Field
                      </button>
                    </div>
                    {embed.fields.map((field, fi) => (
                      <div key={fi} className="bg-dark-900/40 border border-white/[0.04] rounded-md p-2.5 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-[10px]">Field {fi + 1}</span>
                          <button type="button" onClick={() => removeField(ei, fi)}
                            className="p-0.5 text-gray-700 hover:text-red-400 transition-colors">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <input value={field.name} onChange={e => updateField(ei, fi, { name: e.target.value })} placeholder="Name"
                            className="bg-dark-900/60 border border-white/[0.06] rounded px-2 py-1 text-white text-[12px] placeholder-gray-700 outline-none focus:border-primary/40" />
                          <input value={field.value} onChange={e => updateField(ei, fi, { value: e.target.value })} placeholder="Value"
                            className="bg-dark-900/60 border border-white/[0.06] rounded px-2 py-1 text-white text-[12px] placeholder-gray-700 outline-none focus:border-primary/40" />
                        </div>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" checked={field.inline} onChange={e => updateField(ei, fi, { inline: e.target.checked })}
                            className="accent-primary w-3.5 h-3.5" />
                          <span className="text-gray-500 text-[11px]">Inline</span>
                        </label>
                      </div>
                    ))}
                  </div>

                  {/* Images */}
                  <Section label="Images">
                    <div className="grid grid-cols-2 gap-2.5">
                      <Input label="Thumbnail URL" value={embed.thumbnailUrl} onChange={v => updateEmbed(ei, { thumbnailUrl: v })} placeholder="https://..." />
                      <Input label="Image URL" value={embed.imageUrl} onChange={v => updateEmbed(ei, { imageUrl: v })} placeholder="https://..." />
                    </div>
                  </Section>

                  {/* Footer */}
                  <Section label="Footer">
                    <div className="grid grid-cols-2 gap-2.5">
                      <Input label="Text" value={embed.footerText} onChange={v => updateEmbed(ei, { footerText: v })} placeholder="Footer text" />
                      <Input label="Icon URL" value={embed.footerIconUrl} onChange={v => updateEmbed(ei, { footerIconUrl: v })} placeholder="https://..." />
                    </div>
                  </Section>
                </div>
              )}
            </div>
          ))}

          {/* Add Embed button */}
          {embeds.length < 10 && (
            <button type="button" onClick={() => setEmbeds([...embeds, { ...EMPTY_EMBED }])}
              className="w-full flex items-center justify-center gap-1.5 py-2.5 text-[12px] text-gray-500 border border-dashed border-white/[0.08] rounded-lg hover:text-primary hover:border-primary/30 transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add Embed {embeds.length > 0 && `(${embeds.length}/10)`}
            </button>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <button type="button" onClick={handleSend} disabled={sending}
              className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send
            </button>
            <button type="button" onClick={handleEdit} disabled={sending || !editMessageUrl.trim()}
              className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium bg-dark-800/60 text-gray-300 border border-white/[0.08] rounded-lg hover:text-white hover:border-white/[0.12] disabled:opacity-40 transition-colors">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit3 className="w-4 h-4" />}
              Edit Message
            </button>
          </div>

          {/* Status */}
          {status.type !== 'idle' && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] ${status.type === 'success' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/15'
                : 'bg-red-500/10 text-red-400 border border-red-500/15'
              }`}>
              {status.type === 'success' ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 shrink-0" />}
              <span className="truncate">{status.msg}</span>
            </div>
          )}
        </div>

        {/* ─── Preview Column ─── */}
        {previewVisible && (
          <div className="space-y-2">
            <p className="text-gray-600 text-[11px] uppercase tracking-wider font-medium">Live Preview</p>
            <div className="sticky top-4">
              <DiscordPreview content={content} embeds={embeds} username={username} avatarUrl={avatarUrl} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
